/**
 * Argentina's ATS routes — the lower and upper airways — as data.
 *
 * Built by `scripts/build-airways.mjs` from ANAC's own AIP (ENR 3.1, ENR 3.2,
 * ENR 4.4), with every route cross-checked against ENR 4.4's positions and the
 * published distance of every segment; a route that fails is left out, never
 * patched. See that script for what is carried and why.
 *
 * ## What this module will and will not say
 *
 * It says **which published route joins two published points, and through
 * which points**. That is what a pilot needs to draw an airway on the map and
 * to file `FDO W5 ROS` instead of a direct line.
 *
 * Per segment it also carries what ENR 3 prints beside it — the **magnetic
 * track** each way, the **upper and lower limits**, the **minimum flight
 * level** and the **lateral limit** — but only where the script read the
 * segment's own table cell cleanly and the value passed its checks: tracks
 * within 25° of the true course of the segment's two points and reciprocal
 * when two are printed; levels in the expected shape with upper above lower
 * and the minimum not below the lower limit (`scripts/build-airways.mjs`
 * explains each). **Anything else is null, and null is shown as absent** —
 * never filled from a neighbouring segment, a typical value or memory (rule
 * 2). A minimum flight level read wrong is the half of an airway that puts an
 * aircraft into terrain, which is why the script would rather carry nothing.
 *
 * It says nothing about the **direction of cruising levels** (the ODD/EVEN
 * column is drawn as arrow glyphs the text layer cannot read). Every surface
 * that shows an airway shows the edition it came from and sends the pilot to
 * ENR 3 for that.
 *
 * Pure: no fetch, no clock, and no data of its own. The ~240 KiB bundle is
 * **handed in** (`installAirways`) by whoever runs this: the app loads it lazily
 * (`services/airways/load.ts` — its own precached chunk, so still on the device
 * in the air, but no longer parsed on every launch before the first screen),
 * the public API installs it at cold start. Until it is installed every lookup
 * answers as for an unknown point — nothing found — and the app's callers wait
 * for the load before they ask.
 */

export type AirspaceLayer = 'lower' | 'upper'

export interface AirwayPoint {
	ident: string
	kind: 'fix' | 'navaid'
	lat: number
	lon: number
}

/** A level as ENR 3 prints it: `FL 245` or `4500 FT`. Never converted between the two. */
export interface AirwayLevel {
	value: number
	unit: 'FL' | 'FT'
}

/**
 * One segment, from `points[i]` to `points[i + 1]`, as ENR 3 prints it. Every
 * field but the distance is null where the AIP's cell did not read cleanly or
 * did not pass its check — absent, never guessed.
 */
export interface AirwaySegment {
	/** Published distance, NM (always present: a route whose distances do not check is not carried). */
	distanceNm: number
	/** Published magnetic track flying `points[i]` → `points[i + 1]`, degrees (1–360). */
	trackMagDeg: number | null
	/** Published magnetic track the other way, or null when the AIP prints only one. */
	reverseTrackMagDeg: number | null
	upper: AirwayLevel | null
	lower: AirwayLevel | null
	/** Minimum flight level (the AIP's "nivel de vuelo mínimo"). */
	minimum: AirwayLevel | null
	/** Lateral limits, NM either side, when the AIP prints one plain figure (a footnoted one is null). */
	lateralNm: number | null
}

export interface Airway {
	designator: string
	airspace: AirspaceLayer
	/** Conventional (ENR 3.1) or RNAV 5 (ENR 3.2). */
	kind: 'conventional' | 'rnav'
	points: AirwayPoint[]
	/** Published distance of each segment, NM — `points.length - 1` of them. Same as `segments[i].distanceNm`. */
	segmentNm: number[]
	/** `points.length - 1` segments, in order. */
	segments: AirwaySegment[]
}

/** A significant point from ENR 4.4, with what the AIP says it is used on. */
export interface AirwayFix extends AirwayPoint {
	kind: 'fix'
	/** ENR 4.4's usage column (`W5-UW5-SID AER EZE-TMA BAIRES`), ≤ 80 characters, or null. */
	uses: string | null
}

export interface AirwaySource {
	document: string
	edition: string | null
	/** `YYYY-MM-DD`. */
	effective: string | null
}

/** The bundle `scripts/build-airways.mjs` writes (`src/data/airways.json`). */
export interface AirwaysData {
	source: string
	sources: Record<string, AirwaySource>
	generatedAt: string
	routes: {
		designator: string
		airspace: AirspaceLayer
		kind: Airway['kind']
		points: [string, 'F' | 'N', number, number][]
		nm: number[]
		/**
		 * Aligned with `nm`: `[trackMag, reverseTrackMag, upper, lower,
		 * minimum, lateralNm]`, levels as `"FL245"` / `"4500FT"`, any of them
		 * null. Absent in a bundle built before 2026-10-06.
		 */
		seg?: [number | null, number | null, string | null, string | null, string | null, number | null][]
	}[]
	/** `[lat, lon]`, or `[lat, lon, uses]` since 2026-10-06. */
	fixes: Record<string, [number, number] | [number, number, string]>
}

type RawData = AirwaysData

const EMPTY: RawData = { source: '', sources: {}, generatedAt: '', routes: [], fixes: {} }
let raw: RawData = EMPTY

/** `"FL245"` → FL 245, `"4500FT"` → 4500 ft; anything else is null, never a guess. */
function parseLevel(code: string | null | undefined): AirwayLevel | null {
	if (!code) return null
	const match = /^(?:FL(\d{2,3})|(\d{3,5})FT)$/.exec(code)
	if (!match) return null
	return match[1] ? { value: Number(match[1]), unit: 'FL' } : { value: Number(match[2]), unit: 'FT' }
}

function finiteOrNull(value: number | null | undefined): number | null {
	return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function toAirway(route: RawData['routes'][number]): Airway {
	return {
		designator: route.designator,
		airspace: route.airspace,
		kind: route.kind,
		points: route.points.map(([ident, kind, lat, lon]) => ({
			ident,
			kind: kind === 'N' ? 'navaid' : 'fix',
			lat,
			lon,
		})),
		segmentNm: route.nm,
		segments: route.nm.map((distanceNm, index) => {
			const row = route.seg?.[index]
			return {
				distanceNm,
				trackMagDeg: finiteOrNull(row?.[0]),
				reverseTrackMagDeg: finiteOrNull(row?.[1]),
				upper: parseLevel(row?.[2]),
				lower: parseLevel(row?.[3]),
				minimum: parseLevel(row?.[4]),
				lateralNm: finiteOrNull(row?.[5]),
			}
		}),
	}
}

/** Every route carried. Empty until `installAirways`; a live binding, so importers see the install. */
export let AIRWAYS: readonly Airway[] = []
let BY_DESIGNATOR = new Map<string, Airway>()
/** Which routes each point is on, for "leave this point along…". */
let BY_POINT = new Map<string, Airway[]>()

/**
 * Hand the bundle in. Idempotent for the same object; a second, different
 * bundle replaces the first (tests).
 */
export function installAirways(data: unknown): void {
	if (data === raw) return
	raw = data as RawData
	AIRWAYS = raw.routes.map(toAirway)
	BY_DESIGNATOR = new Map(AIRWAYS.map((airway) => [airway.designator, airway]))
	BY_POINT = new Map()
	for (const airway of AIRWAYS) {
		for (const point of airway.points) {
			const list = BY_POINT.get(point.ident) ?? []
			if (!list.includes(airway)) list.push(airway)
			BY_POINT.set(point.ident, list)
		}
	}
}

/** Whether the bundle has been handed in. */
export function airwaysInstalled(): boolean {
	return raw !== EMPTY
}

/** `FL245`, or `4500 ft` — the way the level is printed, never converted. */
export function formatAirwayLevel(level: AirwayLevel): string {
	return level.unit === 'FL' ? `FL${String(level.value).padStart(3, '0')}` : `${level.value} ft`
}

function toFix(ident: string, at: RawData['fixes'][string]): AirwayFix {
	return { ident, kind: 'fix', lat: at[0], lon: at[1], uses: typeof at[2] === 'string' ? at[2] : null }
}

/** The editions this bundle was built from, for every surface that shows an airway. */
export function airwaySources(): { documents: AirwaySource[]; summary: string } {
	const documents = Object.values(raw.sources)
	const routes = documents.filter((document) => document.document.startsWith('ENR-3'))
	const editions = [
		...new Set(routes.map((document) => `${document.edition ?? '?'} ${document.effective ?? ''}`.trim())),
	]
	return { documents, summary: `AIP Argentina ENR 3.1/3.2 · ${editions.join(', ')}` }
}

export function airwayByDesignator(designator: string): Airway | null {
	return BY_DESIGNATOR.get(designator.trim().toUpperCase().replace(/\s+/g, '')) ?? null
}

/**
 * A significant point (five-letter name-code) by name, from ENR 4.4 — every
 * one published, whether or not a route kept in the bundle uses it.
 */
export function findFix(ident: string): AirwayFix | null {
	const code = ident.trim().toUpperCase()
	const at = raw.fixes[code]
	return at ? toFix(code, at) : null
}

/** Every routes a point is on. */
export function airwaysThrough(ident: string): Airway[] {
	return BY_POINT.get(ident.trim().toUpperCase()) ?? []
}

/** Designators on which both points lie. */
export function airwaysJoining(from: string, to: string): Airway[] {
	const target = to.trim().toUpperCase()
	return airwaysThrough(from).filter((airway) => airway.points.some((point) => point.ident === target))
}

/**
 * The points flown along a route from one of its points to another, **both
 * included**, in the direction of flight — the route read backwards when the
 * exit comes before the entry. Null when either is not on it, or they are the
 * same point.
 *
 * Whether the route may be flown in that direction at a given level is the
 * AIP's to say (ENR 3, the direction-of-levels column); this does not decide it.
 */
export function airwaySegment(designator: string, from: string, to: string): AirwayPoint[] | null {
	const airway = airwayByDesignator(designator)
	if (!airway) return null
	const a = airway.points.findIndex((point) => point.ident === from.trim().toUpperCase())
	const b = airway.points.findIndex((point) => point.ident === to.trim().toUpperCase())
	if (a < 0 || b < 0 || a === b) return null
	return a < b ? airway.points.slice(a, b + 1) : airway.points.slice(b, a + 1).reverse()
}

/**
 * Where a pilot can go along each route from a point: for every route through
 * it, the points ahead in each direction. What the planning offers as "leave
 * by airway".
 */
export function exitsFrom(ident: string): { airway: Airway; towards: AirwayPoint[] }[] {
	const code = ident.trim().toUpperCase()
	const out: { airway: Airway; towards: AirwayPoint[] }[] = []
	for (const airway of airwaysThrough(code)) {
		const index = airway.points.findIndex((point) => point.ident === code)
		const ahead = airway.points.slice(index + 1)
		const behind = airway.points.slice(0, index).reverse()
		if (ahead.length) out.push({ airway, towards: ahead })
		if (behind.length) out.push({ airway, towards: behind })
	}
	return out
}

/** Fixes whose name starts with the query, for a search box. Identifiers only. */
export function searchFixes(query: string, limit = 12): AirwayFix[] {
	const code = query.trim().toUpperCase()
	if (code.length < 2) return []
	const out: AirwayFix[] = []
	for (const [ident, at] of Object.entries(raw.fixes)) {
		if (!ident.startsWith(code)) continue
		out.push(toFix(ident, at))
		if (out.length >= limit) break
	}
	return out
}

/** Every significant point ENR 4.4 publishes, with its usage, for the map layer. */
export function allFixes(): AirwayFix[] {
	return Object.entries(raw.fixes).map(([ident, at]) => toFix(ident, at))
}
